// src/components/meta/WhatsAppCoexistenceConnect.tsx
// «Conectar WhatsApp (mantener la app en el teléfono)» — Embedded Signup de
// Meta con COEXISTENCIA: el número sigue funcionando en la app WhatsApp
// Business del teléfono y además queda conectado a la Cloud API (Orión).
//
// Flujo:
//   1. Carga el JS SDK de Facebook con el app_id que da el backend.
//   2. FB.login con el config_id de Embedded Signup (VITE_META_WA_CONFIG_ID,
//      variable de build por despliegue — NO fija en el código) y
//      featureType 'whatsapp_business_app_onboarding'.
//   3. Junta el `code` (callback de FB.login) con waba_id + phone_number_id
//      (mensaje WA_EMBEDDED_SIGNUP de facebook.com). Llegan en cualquier orden.
//   4. POST al backend, que hace el resto (services/whatsapp_embedded_signup.py).
// El org_id lo pone el backend desde el JWT.

import { useCallback, useEffect, useRef, useState } from 'react';
import { AlertTriangle, CheckCircle, Loader2, RefreshCw, Smartphone } from 'lucide-react';

import {
  completeWaSignup, getWaSignupConfig, getWaSignupStatus, WaSignupError,
  type WaSignupConfig, type WaSignupResult, type WaSignupStatus,
} from '@/lib/api_whatsapp_signup';

type Notifier = (t: 'success' | 'error' | 'info', msg: string) => void;

const CONFIG_ID: string = (import.meta.env.VITE_META_WA_CONFIG_ID as string | undefined)?.trim() || '';
const SDK_ID = 'facebook-jssdk';

declare global {
  interface Window {
    FB?: any;
    fbAsyncInit?: () => void;
  }
}

/** Carga el SDK una sola vez por página e inicializa con el app_id del backend. */
function loadFacebookSdk(appId: string, version: string): Promise<any> {
  return new Promise((resolve, reject) => {
    if (window.FB) {
      window.FB.init({ appId, autoLogAppEvents: true, xfbml: false, version });
      resolve(window.FB);
      return;
    }
    window.fbAsyncInit = () => {
      window.FB.init({ appId, autoLogAppEvents: true, xfbml: false, version });
      resolve(window.FB);
    };
    if (!document.getElementById(SDK_ID)) {
      const s = document.createElement('script');
      s.id = SDK_ID;
      s.async = true;
      s.defer = true;
      s.crossOrigin = 'anonymous';
      s.src = 'https://connect.facebook.net/es_LA/sdk.js';
      s.onerror = () => reject(new Error('No se pudo cargar el SDK de Facebook (¿bloqueador de anuncios?).'));
      document.body.appendChild(s);
    }
    window.setTimeout(() => { if (!window.FB) reject(new Error('El SDK de Facebook tardó demasiado en cargar.')); }, 20000);
  });
}

function isFacebookOrigin(origin: string): boolean {
  try {
    const host = new URL(origin).hostname;
    return host === 'facebook.com' || host.endsWith('.facebook.com');
  } catch {
    return false;
  }
}

interface SignupIds { waba_id: string; phone_number_id: string }

export function WhatsAppCoexistenceConnect({ notify, onConnected, showNumbers = true, className = 'max-w-xl' }: {
  notify?: Notifier;
  /** Se llama al terminar de conectar un número (p.ej. para recargar la lista). */
  onConnected?: () => void;
  /** Dentro de la app WhatsApp los números ya se muestran arriba: no repetirlos. */
  showNumbers?: boolean;
  className?: string;
}) {
  const note = (t: Parameters<Notifier>[0], m: string) => notify?.(t, m);

  const [cfg, setCfg] = useState<WaSignupConfig | null>(null);
  const [status, setStatus] = useState<WaSignupStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<WaSignupResult | null>(null);
  const [makeDefault, setMakeDefault] = useState(false);
  const [syncHistory, setSyncHistory] = useState(false);

  // code e ids llegan por canales distintos y en cualquier orden.
  const codeRef = useRef<string | null>(null);
  const idsRef = useRef<SignupIds | null>(null);
  const sentRef = useRef(false);
  const optsRef = useRef({ makeDefault, syncHistory });
  optsRef.current = { makeDefault, syncHistory };
  const onConnectedRef = useRef(onConnected);
  onConnectedRef.current = onConnected;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, s] = await Promise.all([getWaSignupConfig(), getWaSignupStatus()]);
      setCfg(c);
      setStatus(s);
    } catch (e: any) {
      setError(e instanceof WaSignupError ? e.message : e?.message || 'Error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const finish = useCallback(async () => {
    const code = codeRef.current;
    const ids = idsRef.current;
    if (!code || !ids || sentRef.current) return;
    sentRef.current = true;
    try {
      const r = await completeWaSignup({
        code,
        waba_id: ids.waba_id,
        phone_number_id: ids.phone_number_id,
        make_default: optsRef.current.makeDefault,
        sincronizar_historial: optsRef.current.syncHistory,
      });
      setResult(r);
      note('success', 'WhatsApp conectado. El número sigue funcionando en la app del teléfono.');
      await load();
      onConnectedRef.current?.();
    } catch (e: any) {
      const msg = e instanceof WaSignupError ? e.message : e?.message || 'Error al conectar';
      setError(msg);
      note('error', msg);
    } finally {
      setBusy(false);
    }
  }, [load]);

  // Mensajes del popup de Meta (sessionInfoVersion 3).
  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (!isFacebookOrigin(event.origin)) return;
      let data: any = event.data;
      if (typeof data === 'string') {
        try { data = JSON.parse(data); } catch { return; }
      }
      if (!data || data.type !== 'WA_EMBEDDED_SIGNUP') return;
      const ev = String(data.event || '');
      if (ev.startsWith('FINISH')) {
        const waba = String(data.data?.waba_id || '');
        const phone = String(data.data?.phone_number_id || '');
        if (!waba || !phone) {
          setError('Meta terminó el proceso sin indicar el número. Repite la conexión y elige el número de la app.');
          setBusy(false);
          return;
        }
        idsRef.current = { waba_id: waba, phone_number_id: phone };
        finish();
      } else if (ev === 'CANCEL') {
        const paso = data.data?.current_step ? ` (en el paso «${data.data.current_step}»)` : '';
        setError(`Cancelaste la conexión${paso}. Puedes intentarlo de nuevo cuando quieras.`);
        setBusy(false);
      } else if (ev === 'ERROR') {
        setError(`Meta reportó un error: ${data.data?.error_message || 'sin detalle'}.`);
        setBusy(false);
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [finish]);

  const connect = async () => {
    if (!cfg?.enabled || !cfg.app_id) return;
    setError(null);
    setResult(null);
    codeRef.current = null;
    idsRef.current = null;
    sentRef.current = false;
    setBusy(true);
    try {
      const FB = await loadFacebookSdk(cfg.app_id, cfg.graph_version || 'v21.0');
      FB.login((response: any) => {
        const code = response?.authResponse?.code;
        if (code) {
          codeRef.current = code;
          finish();
        } else {
          // Sin code: el usuario cerró el popup o Meta falló. Si llega un
          // mensaje CANCEL/ERROR, ese mensaje gana (es más específico).
          window.setTimeout(() => {
            if (!codeRef.current) {
              setBusy(false);
              setError((prev) => prev || 'No se completó la autorización con Meta.');
            }
          }, 800);
        }
      }, {
        config_id: CONFIG_ID,
        response_type: 'code',
        override_default_response_type: true,
        extras: {
          setup: {},
          featureType: 'whatsapp_business_app_onboarding',
          sessionInfoVersion: '3',
        },
      });
    } catch (e: any) {
      setBusy(false);
      setError(e?.message || 'No se pudo abrir la conexión con Meta.');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-6 justify-center text-muted-foreground text-sm">
        <Loader2 size={14} className="animate-spin" /> Cargando WhatsApp…
      </div>
    );
  }

  const disabledReason = !CONFIG_ID
    ? 'Falta VITE_META_WA_CONFIG_ID en el build de este dashboard.'
    : cfg && !cfg.enabled
      ? (cfg.motivo || 'La conexión no está configurada en el servidor.')
      : null;

  return (
    <div className={`rounded-xl border border-border bg-card text-card-foreground p-4 space-y-3 ${className}`}>
      <div className="flex items-center gap-2">
        <Smartphone size={18} className="text-primary" />
        <h2 className="text-lg font-bold text-foreground">WhatsApp con la app en el teléfono</h2>
      </div>
      <p className="text-sm text-muted-foreground">
        Conecta el número que ya usas en <strong className="text-foreground">WhatsApp Business</strong>.
        Sigue funcionando en tu teléfono y, además, el agente puede contestar. Si respondes tú desde la
        app, el agente se pausa en esa conversación para no contestar encima de ti.
      </p>

      {showNumbers && status && status.numeros.length > 0 && (
        <div className="space-y-1">
          {status.numeros.map((n) => (
            <div key={n.phone_number_id} className="flex items-center gap-2 text-xs rounded-md bg-muted/40 px-2 py-1.5">
              {n.enabled
                ? <CheckCircle size={12} className="text-primary shrink-0" />
                : <AlertTriangle size={12} className="text-muted-foreground shrink-0" />}
              <span className="text-foreground truncate">{n.display_name || n.phone_number_id}</span>
              {n.is_default && <span className="text-[10px] rounded bg-primary/15 text-primary px-1.5">predeterminado</span>}
              {!n.enabled && <span className="text-[10px] text-muted-foreground">deshabilitado</span>}
              <code className="ml-auto text-[10px] text-muted-foreground font-mono">{n.phone_number_id}</code>
            </div>
          ))}
        </div>
      )}

      {status && status.numeros.some((n) => n.enabled && n.is_default) && (
        <label className="flex items-center gap-2 text-xs text-muted-foreground">
          <input type="checkbox" checked={makeDefault} onChange={(e) => setMakeDefault(e.target.checked)} />
          Usar el número nuevo como predeterminado para mensajes salientes
        </label>
      )}
      {cfg?.sync_disponible && (
        <label className="flex items-center gap-2 text-xs text-muted-foreground">
          <input type="checkbox" checked={syncHistory} onChange={(e) => setSyncHistory(e.target.checked)} />
          Pedir a Meta los contactos y el historial de la app (solo en las primeras 24 h)
        </label>
      )}

      <button
        onClick={connect}
        disabled={busy || !!disabledReason}
        className="px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 flex items-center gap-2 w-fit text-sm"
      >
        {busy ? <Loader2 size={14} className="animate-spin" /> : <Smartphone size={14} />}
        Conectar WhatsApp (mantener la app en el teléfono)
      </button>

      {disabledReason && <p className="text-xs text-muted-foreground">{disabledReason}</p>}

      {error && (
        <div className="text-xs rounded-md border border-destructive/30 bg-destructive/10 text-destructive px-3 py-2">
          {error}
        </div>
      )}

      {result && (
        <div className="text-xs rounded-md border border-border bg-muted/40 text-foreground px-3 py-2 space-y-1">
          <div className="flex items-center gap-1.5 font-semibold">
            <CheckCircle size={12} className="text-primary" />
            {result.numero.verified_name || result.numero.display_phone_number || result.numero.phone_number_id} conectado
          </div>
          <p className="text-muted-foreground">{result.nota}</p>
          {result.otras_apps_suscritas.length > 0 && (
            <p className="text-destructive">
              Ojo: esta cuenta también está conectada a {result.otras_apps_suscritas.join(', ')}. Si es un proveedor
              (Twilio, 360dialog…), es posible que no podamos enviar mensajes desde aquí.
            </p>
          )}
          {result.aviso_sincronizacion && <p className="text-muted-foreground">{result.aviso_sincronizacion}</p>}
        </div>
      )}

      <button onClick={load} className="text-[11px] text-muted-foreground hover:text-foreground flex items-center gap-1">
        <RefreshCw size={11} /> Actualizar estado
      </button>
    </div>
  );
}

export default WhatsAppCoexistenceConnect;
